import React, { useMemo } from 'react';
import { Linking } from 'react-native';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  ButtonText,
  Icon,
} from '@gluestack-ui/themed';
import { Layout, Video, ExternalLink } from 'lucide-react-native';
import { Module } from '../types';
import { Booking } from '../../../common/api/models/booking';

interface ActivityCardProps {
  /** The booking data for the activity */
  booking: Booking;
  /** All modules available in the course */
  modules: Module[];
  /** Callback to open a module from the activity card */
  onOpenModule: (params: { moduleId: string; courseId?: string }) => void;
  /** Optional testID for automation */
  testID?: string;
}

/**
 * ActivityCard displays an upcoming study session or group activity.
 */
export const ActivityCard: React.FC<ActivityCardProps> = ({
  booking, modules, onOpenModule, testID,
}) => {
  const bookingDate = new Date(booking.startTime);
  const now = new Date();
  const isToday = bookingDate.toDateString() === now.toDateString();

  const diffTime = bookingDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const startTime = bookingDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const dateStr = bookingDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  const relativeStr = isToday ? 'TODAY' : diffDays === 1 ? 'TOMORROW' : `IN ${diffDays} DAYS`;

  const moduleInfo = useMemo(() => {
    const module = modules.find((m) => m.id === booking.moduleId);
    // Note: The original logic for task lookup was slightly different, adapting it to available types.
    return {
      moduleTitle: module?.title || 'Unknown Module',
      taskTitle: 'Group Study Session', // Fallback as task structure varies
      moduleIndex: module?.originalIndex,
      courseId: module?.courses?.[0]?.id,
    };
  }, [booking.moduleId, modules]);

  const handleJoin = () => {
    if (booking.videoMeetingJoinUrl) {
      Linking.openURL(booking.videoMeetingJoinUrl);
    }
  };

  return (
    <Box
      bg="$bgCard"
      p="$4"
      borderRadius="$md"
      borderWidth={1}
      borderColor="$borderLight"
      w="100%"
      testID={testID}
    >
      <VStack space="sm">
        <HStack justifyContent="space-between" alignItems="center">
          <HStack space="xs" alignItems="center">
            <Box bg={isToday ? '$statusErrorSubtle' : '$bgOverlay'} px="$2" py="$0.5" borderRadius="$md">
              <Text size="2xs" fontWeight="900" color={isToday ? '$textOnErrorSubtle' : '$textSubtle'}>
                {relativeStr}
              </Text>
            </Box>
            <Text size="2xs" color="$textSubtle" fontWeight="bold">
              {dateStr}
            </Text>
          </HStack>
          <Text size="2xs" color="$textSubtle" fontWeight="bold">
            {startTime}
          </Text>
        </HStack>

        <VStack space="xs">
          <Heading size="xs" color="$textHeading" numberOfLines={1}>
            {moduleInfo.taskTitle}
          </Heading>
          <Text size="2xs" color="$textSubtle" numberOfLines={1}>
            {moduleInfo.moduleTitle}
          </Text>
        </VStack>

        <HStack space="sm" mt="$1">
          <Button
            size="xs"
            variant="outline"
            borderColor="$borderBrand"
            borderRadius="$full"
            action="primary"
            flex={1}
            onPress={() => onOpenModule({ moduleId: booking.moduleId, courseId: moduleInfo.courseId })}
          >
            <HStack space="xs" alignItems="center">
              <Icon as={Layout} size="2xs" color="$brandBase" />
              <ButtonText color="$brandBase" size="xs">
                MODULE
              </ButtonText>
            </HStack>
          </Button>

          {!!booking.videoMeetingJoinUrl && (
            <Button size="xs" bg="$brandBase" borderRadius="$full" flex={1} onPress={handleJoin}>
              <HStack space="xs" alignItems="center">
                <Icon as={Video} size="2xs" color="$textInverted" />
                <ButtonText color="$textInverted" size="xs">
                  JOIN
                </ButtonText>
                <Icon as={ExternalLink} size="2xs" color="$textInverted" />
              </HStack>
            </Button>
          )}
        </HStack>
      </VStack>
    </Box>
  );
};
